/**
 * A drawing from the shop's library, turned into a layer.
 *
 * The shop uploads SVG; the card gets paths. That distinction is the whole
 * point: a clipart placed as a picture is a picture - it softens when it is
 * scaled, it prints as pixels, and its colours are whatever the file said. Read
 * as outlines, the same drawing scales without loss, separates as vector on the
 * press, and every colour in it is ink a customer can change.
 *
 * What is understood: path, rect, circle, ellipse, line, polyline, polygon and
 * the groups above them, with transforms, fills, strokes and opacity, from
 * attributes or an inline style. What is not: gradients, filters, clipping,
 * masks, patterns, embedded pictures, text that has not been converted to
 * outlines, and stylesheets. Those are refused with a reason rather than drawn
 * wrongly - a clipart that arrives looking almost right is worse than one the
 * shop is told to export again.
 */

import type { Colour, GraphicLayer, Stroke } from '../model/types';
import { IDENTITY, bounds, multiply, parsePath, parseTransform, toPathData, transformSubPaths, type Matrix, type SubPath } from './path';

/** What the shop's file could not do, in words a shop can act on. */
export class GraphicProblem extends Error {}

interface Piece {
  paths: SubPath[];
  fill: Colour | null;
  stroke: Stroke | null;
  evenOdd: boolean;
  opacity: number;
}

/** Elements we know how to turn into outlines. */
const SHAPES = [ 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon' ];

/** Elements that mean the drawing needs exporting again rather than importing. */
const REFUSED: Record<string, string> = {
  image: 'a picture inside it',
  text: 'live text',
  tspan: 'live text',
  filter: 'a filter',
  lineargradient: 'a gradient',
  radialgradient: 'a gradient',
  pattern: 'a pattern',
  mask: 'a mask',
  clippath: 'a clipping path',
  foreignobject: 'something that is not a drawing',
};

/**
 * Reads an SVG file into one graphic layer, ready to place.
 *
 * @param text The file.
 * @param name What the library calls it.
 * @throws GraphicProblem when the file uses something that cannot be printed.
 */
export function importSvg( text: string, name = '' ): { layer: Omit<GraphicLayer, 'id' | 'x' | 'y' | 'w' | 'h'>; ratio: number } {
  const doc = new DOMParser().parseFromString( text, 'image/svg+xml' );
  const root = doc.documentElement;

  if ( ! root || root.nodeName === 'parsererror' || root.nodeName.toLowerCase() !== 'svg' ) {
    throw new GraphicProblem( 'That file is not a drawing we can read.' );
  }

  for ( const [ tag, what ] of Object.entries( REFUSED ) ) {
    if ( root.getElementsByTagName( tag ).length > 0 ) {
      throw new GraphicProblem( `This drawing has ${ what } in it. Export it again with everything flattened to outlines.` );
    }
  }

  const pieces: Piece[] = [];

  walk( root, IDENTITY, { fill: '#000000', stroke: '', strokeWidth: 1, opacity: 1 }, pieces );

  if ( pieces.length === 0 ) {
    throw new GraphicProblem( 'There is nothing in that drawing we can use.' );
  }

  // Everything it covers, so the layer's box is the drawing's own shape.
  const box = bounds( pieces.flatMap( ( piece ) => piece.paths ) );

  if ( ! box || box.w <= 0 || box.h <= 0 ) {
    throw new GraphicProblem( 'That drawing has no size.' );
  }

  /*
   * Kept from 0 to 1 inside the layer's box. Resizing the layer then costs
   * nothing - the box changes and the drawing follows it - and the same
   * numbers serve the canvas, the proof and the press file.
   */
  const unit: Matrix = [ 1 / box.w, 0, 0, 1 / box.h, -box.x / box.w, -box.y / box.h ];

  /*
   * A stroke goes into the same box.
   *
   * The outlines are squeezed into a unit square, which is what makes resizing
   * free - and a stroke width left in the file's own units would then be drawn
   * as millimetres, which is how a hairline round a logo arrives two
   * millimetres thick. A stroke has one width in both directions, so it is
   * measured against the box's geometric mean and given back the same way when
   * it is drawn (press/draw.ts).
   */
  const across = Math.sqrt( box.w * box.h ) || 1;

  return {
    ratio: box.w / box.h,
    layer: {
      type: 'graphic',
      name: name || 'Graphic',
      source: name,
      rotation: 0,
      opacity: 1,
      locked: false,
      hidden: false,
      paths: pieces.map( ( piece ) => ( {
        d: toPathData( transformSubPaths( piece.paths, unit ), 5 ),
        fill: piece.fill,
        stroke: piece.stroke ? { ...piece.stroke, width: piece.stroke.width / across } : null,
        ...( piece.evenOdd ? { evenOdd: true } : {} ),
      } ) ),
    },
  };
}

interface Inherited {
  fill: string;
  stroke: string;
  strokeWidth: number;
  opacity: number;
}

function walk( element: Element, parent: Matrix, inherited: Inherited, out: Piece[] ): void {
  const matrix = multiply( parent, parseTransform( element.getAttribute( 'transform' ) ) );
  const style = readStyle( element, inherited );
  const tag = element.nodeName.toLowerCase();

  if ( SHAPES.includes( tag ) ) {
    const d = outlineOf( element, tag );

    if ( d ) {
      const paths = transformSubPaths( parsePath( d ), matrix );

      if ( paths.length > 0 && ( style.fill !== 'none' || style.stroke !== 'none' ) ) {
        out.push( {
          paths,
          fill: 'none' === style.fill ? null : ink( style.fill ),
          stroke:
            'none' === style.stroke || ! style.stroke
              ? null
              : {
                  colour: ink( style.stroke ) ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] },
                  // The stroke is in the file's own units; the drawing is
                  // scaled into the layer's box, so the width goes with it.
                  width: style.strokeWidth * scaleOf( matrix ),
                  dash: [],
                },
          evenOdd: 'evenodd' === ( element.getAttribute( 'fill-rule' ) ?? '' ).toLowerCase(),
          opacity: style.opacity,
        } );
      }
    }
  }

  for ( const child of Array.from( element.children ) ) {
    walk( child, matrix, style, out );
  }
}

/** How much a matrix scales, on average - for stroke widths. */
function scaleOf( m: Matrix ): number {
  return ( Math.hypot( m[ 0 ], m[ 1 ] ) + Math.hypot( m[ 2 ], m[ 3 ] ) ) / 2;
}

/** Colour and opacity, from attributes or an inline style, with what it inherits. */
function readStyle( element: Element, inherited: Inherited ): Inherited {
  const inline: Record<string, string> = {};

  for ( const rule of ( element.getAttribute( 'style' ) ?? '' ).split( ';' ) ) {
    const [ key, value ] = rule.split( ':' );

    if ( key && value ) inline[ key.trim().toLowerCase() ] = value.trim();
  }

  const read = ( name: string ) => inline[ name ] ?? element.getAttribute( name ) ?? '';

  const opacity = read( 'opacity' );

  return {
    fill: read( 'fill' ) || inherited.fill,
    stroke: read( 'stroke' ) || inherited.stroke,
    strokeWidth: Number( read( 'stroke-width' ) ) > 0 ? Number( read( 'stroke-width' ) ) : inherited.strokeWidth,
    opacity: opacity ? Math.max( 0, Math.min( 1, Number( opacity ) ) ) : inherited.opacity,
  };
}

/** A colour from the file, as ink. Named colours beyond black and white are not read. */
function ink( value: string ): Colour | null {
  const said = value.trim().toLowerCase();

  if ( ! said || 'none' === said || said.startsWith( 'url(' ) ) return null;

  if ( 'black' === said ) return { space: 'cmyk', value: [ 0, 0, 0, 1 ] };
  if ( 'white' === said ) return { space: 'cmyk', value: [ 0, 0, 0, 0 ] };

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec( said );

  if ( hex ) {
    const short = hex[ 1 ]!.length === 3;
    const full = short ? hex[ 1 ]!.split( '' ).map( ( c ) => c + c ).join( '' ) : hex[ 1 ]!;

    return { space: 'rgb', value: `#${ full }` };
  }

  const rgb = /^rgba?\(([^)]+)\)$/.exec( said );

  if ( rgb ) {
    const parts = rgb[ 1 ]!.split( /[\s,/]+/ ).filter( Boolean ).map( Number );
    const byte = ( value: number ) => Math.max( 0, Math.min( 255, Math.round( value ) ) ).toString( 16 ).padStart( 2, '0' );

    if ( parts.length >= 3 ) return { space: 'rgb', value: `#${ byte( parts[ 0 ]! ) }${ byte( parts[ 1 ]! ) }${ byte( parts[ 2 ]! ) }` };
  }

  // Anything else - a colour name, a variable - is drawn in black rather than
  // silently dropped, so the customer can see it and recolour it.
  return { space: 'cmyk', value: [ 0, 0, 0, 1 ] };
}

/** An element's outline as path data, whatever kind of element it is. */
function outlineOf( element: Element, tag: string ): string {
  const number = ( name: string, fallback = 0 ) => {
    const raw = Number( element.getAttribute( name ) );

    return Number.isFinite( raw ) ? raw : fallback;
  };

  switch ( tag ) {
    case 'path':
      return element.getAttribute( 'd' ) ?? '';

    case 'rect': {
      const x = number( 'x' );
      const y = number( 'y' );
      const w = number( 'width' );
      const h = number( 'height' );
      const rx = Math.min( number( 'rx', number( 'ry' ) ), w / 2 );
      const ry = Math.min( number( 'ry', number( 'rx' ) ), h / 2 );

      if ( w <= 0 || h <= 0 ) return '';

      if ( rx <= 0 || ry <= 0 ) {
        return `M ${ x } ${ y } H ${ x + w } V ${ y + h } H ${ x } Z`;
      }

      return (
        `M ${ x + rx } ${ y } H ${ x + w - rx } A ${ rx } ${ ry } 0 0 1 ${ x + w } ${ y + ry }` +
        ` V ${ y + h - ry } A ${ rx } ${ ry } 0 0 1 ${ x + w - rx } ${ y + h }` +
        ` H ${ x + rx } A ${ rx } ${ ry } 0 0 1 ${ x } ${ y + h - ry }` +
        ` V ${ y + ry } A ${ rx } ${ ry } 0 0 1 ${ x + rx } ${ y } Z`
      );
    }

    case 'circle':
    case 'ellipse': {
      const cx = number( 'cx' );
      const cy = number( 'cy' );
      const rx = 'circle' === tag ? number( 'r' ) : number( 'rx' );
      const ry = 'circle' === tag ? number( 'r' ) : number( 'ry' );

      if ( rx <= 0 || ry <= 0 ) return '';

      return (
        `M ${ cx - rx } ${ cy } A ${ rx } ${ ry } 0 1 0 ${ cx + rx } ${ cy }` +
        ` A ${ rx } ${ ry } 0 1 0 ${ cx - rx } ${ cy } Z`
      );
    }

    case 'line':
      return `M ${ number( 'x1' ) } ${ number( 'y1' ) } L ${ number( 'x2' ) } ${ number( 'y2' ) }`;

    case 'polyline':
    case 'polygon': {
      const points = ( element.getAttribute( 'points' ) ?? '' ).split( /[\s,]+/ ).filter( Boolean ).map( Number );

      if ( points.length < 4 ) return '';

      const steps = [ `M ${ points[ 0 ] } ${ points[ 1 ] }` ];

      for ( let i = 2; i + 1 < points.length; i += 2 ) steps.push( `L ${ points[ i ] } ${ points[ i + 1 ] }` );

      if ( 'polygon' === tag ) steps.push( 'Z' );

      return steps.join( ' ' );
    }

    default:
      return '';
  }
}
